import { useEffect, useRef } from 'react';
import { useInView } from 'framer-motion';
import { useAnimation } from 'framer-motion';

/**
 * Hook that ensures elements are revealed once and stay visible.
 * 
 * This prevents the common bug where elements disappear when scrolled
 * out of view due to whileInView resetting to hidden state.
 * 
 * Usage:
 * ```tsx
 * const { ref, controls } = useRevealOnce({ amount: 0.3 });
 * 
 * <motion.div
 *   ref={ref}
 *   initial="hidden"
 *   animate={controls}
 *   variants={gridContainerVariants}
 * >
 *   ...
 * </motion.div>
 * ```
 * 
 * @param options - Configuration options
 * @param options.amount - Threshold for intersection (0-1), default 0.3
 * @param options.once - Only trigger once (default: true, recommended)
 * @returns Object with ref and animation controls
 */
export function useRevealOnce(options: { amount?: number; once?: boolean } = {}) {
  const { amount = 0.3, once = true } = options;
  const ref = useRef(null);
  const isInView = useInView(ref, { amount, once });
  const controls = useAnimation();
  const hasRevealed = useRef(false);

  useEffect(() => {
    if (isInView && !hasRevealed.current) {
      hasRevealed.current = true;
      controls.start('show');
    } else if (isInView && hasRevealed.current) {
      // Keep showing if already revealed, even if scrolled back into view
      controls.start('show');
    }
    // Intentionally do NOT reset to 'hidden' when out of view
    // This is the key fix - once revealed, stay revealed
  }, [isInView, controls]);

  return { ref, controls };
}

